import { Dialog as DialogPrimitives } from 'radix-ui'
import type { ReactNode } from 'react'
import { cn } from '../cn'

export interface DialogShellProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Extra classes for the glass panel (width, z-index, animations, …). */
  className?: string
  /** Extra classes for the overlay (z-index, animations, …). */
  overlayClassName?: string
  children: ReactNode
}

/**
 * The Forest glass dialog frame: centered panel with gradient outline and
 * blurred overlay. Outside taps never dismiss; dismissal goes through
 * `onOpenChange(false)` (Escape), so composers gate it there (e.g. while a
 * mutation is in flight).
 */
export function DialogShell(props: DialogShellProps) {
  return (
    <DialogPrimitives.Root open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay
          className={cn(
            'fixed inset-0 z-[70] bg-black/10 backdrop-blur-lg',
            props.overlayClassName,
          )}
        />
        <DialogPrimitives.Content
          asChild
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
        >
          <div
            // tailwindcss-animate's animate-in/out (behind a data-[state=…]
            // variant) resets --tw-enter/exit-translate-* with higher
            // specificity than any utility class, dropping the centering
            // translate during enter/exit. Inline style outranks it, so
            // animated consumers fade in place instead of sliding from the
            // corner.
            style={{
              ['--tw-enter-translate-x' as string]: '-50%',
              ['--tw-enter-translate-y' as string]: '-50%',
              ['--tw-exit-translate-x' as string]: '-50%',
              ['--tw-exit-translate-y' as string]: '-50%',
            }}
            className={cn(
              'fixed left-1/2 top-1/2 z-[70] -translate-x-1/2 -translate-y-1/2',
              'rounded-[32px] bg-white/80 backdrop-blur-[50px] will-change-[backdrop-filter]',
              // Gradient outline drawn by a masked ::before ring (the
              // .gradient-outline effect from focus-pass, inlined so consumers
              // need no extra CSS).
              'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:p-[2px]',
              'before:[background:linear-gradient(90deg,#ffffff4d,#ffffff1a)]',
              'before:[-webkit-mask:linear-gradient(#fff_0_0)_content-box,linear-gradient(#fff_0_0)]',
              'before:[-webkit-mask-composite:xor]',
              'before:[mask-composite:exclude]',
              props.className,
            )}
          >
            {props.children}
          </div>
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Root>
  )
}
